import {
  StructuredLogger,
  logFailureReason,
} from '@brighthub/structured-logging';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  OnDestroy,
  signal,
  ViewEncapsulation,
  ViewChild,
} from '@angular/core';
import { SentryService } from '@brighthub/logging';
import { Button } from 'primeng/button';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputTextModule } from 'primeng/inputtext';
import type { PDFDocumentLoadingTask, PDFDocumentProxy } from 'pdfjs-dist';

export interface CreditReportDialogData {
  report: Blob;
}

interface HighlightRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

interface IndexedTextSegment extends HighlightRect {
  start: number;
  end: number;
}

interface IndexedPage {
  text: string;
  segments: IndexedTextSegment[];
}

interface SearchMatch {
  pageNumber: number;
  rects: HighlightRect[];
}

const MIN_SEARCH_LENGTH = 2;
const MAX_SEARCH_MATCHES = 500;
const SEARCH_DEBOUNCE_MS = 150;

type CreditReportRenderStage =
  | 'load_pdfjs'
  | 'read_report'
  | 'load_document'
  | 'load_page'
  | 'extract_text'
  | 'create_canvas'
  | 'render_page';

@Component({
  selector: 'bn-credit-report-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [Button, InputTextModule],
  templateUrl: './credit-report-dialog.component.html',
  styleUrl: './credit-report-dialog.component.scss',
})
export class CreditReportDialogComponent implements AfterViewInit, OnDestroy {
  private readonly _ref = inject(DynamicDialogRef);
  private readonly _config = inject(DynamicDialogConfig);
  private readonly _logs = inject(StructuredLogger);
  private readonly _sentry = inject(SentryService);
  private readonly _data = this._config.data as CreditReportDialogData;
  private _loadingTask: PDFDocumentLoadingTask | null = null;
  private _pdf: PDFDocumentProxy | null = null;
  private readonly _pageIndexes: IndexedPage[] = [];
  private readonly _pageElements = new Map<number, HTMLElement>();
  private _destroyed = false;
  private _searchTimer: ReturnType<typeof setTimeout> | null = null;

  @ViewChild('pages', { static: true })
  private readonly _pages!: ElementRef<HTMLDivElement>;

  readonly loading = signal(true);
  readonly error = signal(false);
  readonly searchQuery = signal('');
  readonly searchMatches = signal<readonly SearchMatch[]>([]);
  readonly activeMatchIndex = signal(-1);
  readonly searchPending = signal(false);
  readonly searchResultsCapped = signal(false);
  readonly hasSearchMatches = computed(() => this.searchMatches().length > 0);
  readonly searchStatus = computed(() => {
    const query = this.searchQuery().trim();
    if (!query) return 'Search this report';
    if (query.length < MIN_SEARCH_LENGTH) return 'Enter 2+ characters';
    if (this.searchPending()) return 'Searching…';
    const matches = this.searchMatches();
    if (!matches.length) return 'No matches';
    return `${this.activeMatchIndex() + 1} of ${matches.length}${
      this.searchResultsCapped() ? '+' : ''
    }`;
  });

  ngAfterViewInit(): void {
    void this._renderReport();
  }

  close(): void {
    this._ref.close();
  }

  queueSearch(query: string): void {
    this.searchQuery.set(query);
    if (this._searchTimer !== null) {
      clearTimeout(this._searchTimer);
      this._searchTimer = null;
    }
    if (query.trim().length < MIN_SEARCH_LENGTH) {
      this.search(query);
      return;
    }
    this.searchPending.set(true);
    this._searchTimer = setTimeout(() => {
      this._searchTimer = null;
      this.search(query);
    }, SEARCH_DEBOUNCE_MS);
  }

  search(query: string): void {
    this.searchQuery.set(query);
    const needle = query.trim().toLocaleLowerCase();
    this.searchPending.set(false);
    this.searchResultsCapped.set(false);
    if (needle.length < MIN_SEARCH_LENGTH) {
      this.searchMatches.set([]);
      this.activeMatchIndex.set(-1);
      this._markActivePage(null);
      this._renderHighlights();
      return;
    }

    const matches: SearchMatch[] = [];
    searchPages: for (const [index, page] of this._pageIndexes.entries()) {
      let offset = 0;
      while ((offset = page.text.indexOf(needle, offset)) !== -1) {
        matches.push({
          pageNumber: index + 1,
          rects: this._highlightRects(page, offset, needle.length),
        });
        offset += needle.length;
        if (matches.length === MAX_SEARCH_MATCHES) {
          this.searchResultsCapped.set(true);
          break searchPages;
        }
      }
    }
    this.searchMatches.set(matches);
    this.activeMatchIndex.set(matches.length ? 0 : -1);
    this._focusActiveMatch();
  }

  previousMatch(): void {
    const matches = this.searchMatches();
    if (!matches.length) return;
    this.activeMatchIndex.update(
      (index) => (index - 1 + matches.length) % matches.length,
    );
    this._focusActiveMatch();
  }

  nextMatch(): void {
    const matches = this.searchMatches();
    if (!matches.length) return;
    this.activeMatchIndex.update((index) => (index + 1) % matches.length);
    this._focusActiveMatch();
  }

  ngOnDestroy(): void {
    this._destroyed = true;
    if (this._searchTimer !== null) clearTimeout(this._searchTimer);
    void this._loadingTask?.destroy();
    void this._pdf?.destroy();
  }

  private async _renderReport(): Promise<void> {
    let stage: CreditReportRenderStage = 'load_pdfjs';
    let pageNumber: number | null = null;
    try {
      const { getDocument, GlobalWorkerOptions } = await import('pdfjs-dist');
      GlobalWorkerOptions.workerSrc = new URL(
        'assets/pdfjs/pdf.worker.min.mjs',
        document.baseURI,
      ).toString();
      stage = 'read_report';
      const data = new Uint8Array(await this._data.report.arrayBuffer());
      if (this._destroyed) return;
      stage = 'load_document';
      this._loadingTask = getDocument({ data });
      this._pdf = await this._loadingTask.promise;

      for (pageNumber = 1; pageNumber <= this._pdf.numPages; pageNumber++) {
        if (this._destroyed) return;
        stage = 'load_page';
        const page = await this._pdf.getPage(pageNumber);
        stage = 'extract_text';
        const textContent = await page.getTextContent();
        const viewport = page.getViewport({ scale: 1.5 });
        let pageText = '';
        const segments: IndexedTextSegment[] = [];
        let previousItem:
          | {
              right: number;
              baseline: number;
              height: number;
              hasEol: boolean;
            }
          | undefined;
        textContent.items.forEach((item) => {
          if (!('str' in item) || !item.str) return;
          const transform = this._transform(
            viewport.transform as number[],
            item.transform,
          );
          const height = Math.hypot(transform[2], transform[3]);
          const left = transform[4];
          const width = Math.abs(item.width) * viewport.scale;
          const normalized = item.str.toLocaleLowerCase().replace(/\s+/g, ' ');
          if (
            pageText &&
            !pageText.endsWith(' ') &&
            !normalized.startsWith(' ') &&
            previousItem &&
            this._needsTextSeparator(previousItem, left, transform[5], height)
          ) {
            pageText += ' ';
          }
          const start = pageText.length;
          pageText += normalized;
          segments.push({
            start,
            end: start + normalized.length,
            left: (left / viewport.width) * 100,
            top: ((transform[5] - height) / viewport.height) * 100,
            width: (width / viewport.width) * 100,
            height: (height / viewport.height) * 100,
          });
          previousItem = {
            right: left + width,
            baseline: transform[5],
            height,
            hasEol: item.hasEOL,
          };
        });
        this._pageIndexes[pageNumber - 1] = { text: pageText, segments };
        stage = 'create_canvas';
        const pageElement = document.createElement('div');
        pageElement.className = 'bn-credit-report__page';
        pageElement.dataset['pageNumber'] = String(pageNumber);
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d', { alpha: false });
        if (!context) throw new Error('Canvas rendering is unavailable');
        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);
        canvas.style.aspectRatio = `${viewport.width} / ${viewport.height}`;
        canvas.setAttribute('aria-label', `Credit report page ${pageNumber}`);
        const highlights = document.createElement('div');
        highlights.className = 'bn-credit-report__highlights';
        highlights.setAttribute('aria-hidden', 'true');
        pageElement.append(canvas);
        pageElement.append(highlights);
        this._pages.nativeElement.append(pageElement);
        this._pageElements.set(pageNumber, pageElement);
        stage = 'render_page';
        await page.render({ canvas, canvasContext: context, viewport }).promise;
        page.cleanup();
      }
    } catch (error) {
      if (!this._destroyed) {
        this._captureRenderFailure(error, stage, pageNumber);
        this._clearRenderedReport();
        this.error.set(true);
      }
    } finally {
      if (!this._destroyed) this.loading.set(false);
    }
  }

  private _focusActiveMatch(): void {
    const pageNumber =
      this.searchMatches()[this.activeMatchIndex()]?.pageNumber ?? null;
    this._markActivePage(pageNumber);
    this._renderHighlights();
    if (pageNumber === null) return;
    this._pageElements
      .get(pageNumber)
      ?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  }

  private _markActivePage(pageNumber: number | null): void {
    this._pageElements.forEach((element, number) => {
      element.classList.toggle(
        'bn-credit-report__page--match',
        number === pageNumber,
      );
    });
  }

  private _highlightRects(
    page: IndexedPage,
    matchStart: number,
    matchLength: number,
  ): HighlightRect[] {
    const matchEnd = matchStart + matchLength;
    return page.segments.flatMap((segment) => {
      const overlapStart = Math.max(matchStart, segment.start);
      const overlapEnd = Math.min(matchEnd, segment.end);
      if (overlapStart >= overlapEnd) return [];
      const characterCount = segment.end - segment.start;
      const startFraction = (overlapStart - segment.start) / characterCount;
      const widthFraction = (overlapEnd - overlapStart) / characterCount;
      return [
        {
          left: segment.left + segment.width * startFraction,
          top: segment.top,
          width: segment.width * widthFraction,
          height: segment.height,
        },
      ];
    });
  }

  private _needsTextSeparator(
    previous: {
      right: number;
      baseline: number;
      height: number;
      hasEol: boolean;
    },
    left: number,
    baseline: number,
    height: number,
  ): boolean {
    if (previous.hasEol) return true;
    const sameLine =
      Math.abs(previous.baseline - baseline) <=
      Math.max(previous.height, height) * 0.5;
    if (!sameLine) return true;
    return left - previous.right > Math.max(1, height * 0.12);
  }

  private _renderHighlights(): void {
    this._pageElements.forEach((pageElement) => {
      pageElement
        .querySelector('.bn-credit-report__highlights')
        ?.replaceChildren();
    });
    const activeIndex = this.activeMatchIndex();
    const activePage = this.searchMatches()[activeIndex]?.pageNumber;
    this.searchMatches().forEach((match, matchIndex) => {
      if (match.pageNumber !== activePage) return;
      const layer = this._pageElements
        .get(match.pageNumber)
        ?.querySelector<HTMLElement>('.bn-credit-report__highlights');
      if (!layer) return;
      match.rects.forEach((rect) => {
        const highlight = document.createElement('span');
        highlight.className = 'bn-credit-report__highlight';
        highlight.classList.toggle(
          'bn-credit-report__highlight--active',
          matchIndex === activeIndex,
        );
        highlight.dataset['matchIndex'] = String(matchIndex);
        highlight.style.left = `${rect.left}%`;
        highlight.style.top = `${rect.top}%`;
        highlight.style.width = `${rect.width}%`;
        highlight.style.height = `${rect.height}%`;
        layer.append(highlight);
      });
    });
  }

  private _clearRenderedReport(): void {
    if (this._searchTimer !== null) {
      clearTimeout(this._searchTimer);
      this._searchTimer = null;
    }
    this._pageIndexes.length = 0;
    this._pageElements.clear();
    this._pages.nativeElement.replaceChildren();
    this.searchQuery.set('');
    this.searchMatches.set([]);
    this.activeMatchIndex.set(-1);
    this.searchPending.set(false);
    this.searchResultsCapped.set(false);
  }

  private _safeErrorType(error: unknown): string {
    if (!(error instanceof Error)) return typeof error;
    return /^[A-Za-z0-9_.-]{1,80}$/.test(error.name) ? error.name : 'Error';
  }

  private _captureRenderFailure(
    error: unknown,
    stage: CreditReportRenderStage,
    pageNumber: number | null,
  ): void {
    this._logs.emit('feature.operation.failed', {
      feature: 'credit_overview',
      stage,
      trigger: 'user',
      outcome: 'failed',
      ...logFailureReason(error),
    });
    try {
      this._sentry.captureErrorWithExtras(
        new Error('Credit report rendering failed'),
        {
          stage,
          pageNumber,
          errorType: this._safeErrorType(error),
        },
      );
    } catch {
      // Telemetry must never prevent the safe all-or-nothing error state.
    }
  }

  private _transform(first: number[], second: number[]): number[] {
    return [
      first[0] * second[0] + first[2] * second[1],
      first[1] * second[0] + first[3] * second[1],
      first[0] * second[2] + first[2] * second[3],
      first[1] * second[2] + first[3] * second[3],
      first[0] * second[4] + first[2] * second[5] + first[4],
      first[1] * second[4] + first[3] * second[5] + first[5],
    ];
  }
}
